import type { AIStore } from '..';
import type { AIConfigsRepresentationType, UpdateAIConfigsRepresentationType } from '@readme/api/src/mappings/ai/types';
import type { StateCreator } from 'zustand';

import produce from 'immer';

import type useReadmeApi from '@core/hooks/useReadmeApi';
import { fetcher } from '@core/hooks/useReadmeApi';
import { actionLog } from '@core/store/util';

export type LinterSettingsNames = 'errors' | 'styleguide' | 'warnings';

export type AiLinterSliceState = AIConfigsRepresentationType['data'] & {
  /**
   * Path to the API /ai/configs url including the correct baseUrl.
   */
  apiUrl?: string;
  /**
   * Whether the store has initialized with it's initial data.
   */
  isReady: boolean;
  /**
   * Wether any of the linting configs are currently saving.
   */
  isSaving: {
    errors?: boolean;
    warnings?: boolean;
    styleguide?: boolean;
  };
  /**
   * SWR mutate function bound to the request made in the <InitializeAILinter /> component.
   */
  mutate?: ReturnType<typeof useReadmeApi<AIConfigsRepresentationType>>['mutate'];
};

interface AiLinterSliceAction {
  initialize: (
    args: Required<Pick<AiLinterSliceState, 'mutate' | 'settings'>> & {
      apiBaseUrl: string;
    },
  ) => void;
  /*
   * Updates the prompt for a specific settings ('errors', 'warnings',
   * 'styleguide'). Will send a PUT request to the API and modify the loading
   * state while the request is in progress.
   */
  updatePrompt: (name: keyof AiLinterSliceState['settings'], value: string | null) => void;
}

export interface AiLinterSlice {
  linter: AiLinterSliceAction & AiLinterSliceState;
}

const initialState: AiLinterSliceState = {
  isReady: false,
  enabled: true,
  settings: {
    errors: null,
    warnings: null,
    styleguide: null,
  },
  isSaving: {
    errors: true,
    warnings: true,
    styleguide: true,
  },
};

export const createAiLinterSlice: StateCreator<
  AIStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  AiLinterSlice
> = (set, get) => ({
  linter: {
    ...initialState,

    initialize: ({ apiBaseUrl, settings, mutate }) => {
      set(
        state => {
          state.linter.apiUrl = `${apiBaseUrl}/ai/configs`;
          state.linter.settings = settings;
          state.linter.isSaving = {
            errors: false,
            warnings: false,
            styleguide: false,
          };
          state.linter.mutate = mutate;
          state.linter.isReady = true;
        },
        false,
        actionLog('ai.linter.initialize', [apiBaseUrl, settings, mutate]),
      );
    },

    updatePrompt: async (name, value) => {
      const apiUrl = get().linter.apiUrl;
      const mutate = get().linter.mutate;
      if (!apiUrl || get().linter.isSaving[name] || !mutate) {
        return;
      }

      set(
        state => {
          state.linter.isSaving[name] = true;
        },
        false,
        actionLog('ai.linter.updatePrompt', [name, 'isSaving']),
      );

      try {
        const request = fetcher<UpdateAIConfigsRepresentationType>(apiUrl, {
          body: JSON.stringify({ data: { settings: { [name]: value } } }),
          method: 'PUT',
        });

        const getNewCacheValue = request.then(({ data }) => ({
          data: produce(get().linter, draft => {
            draft.settings[name] = data.settings[name] || null;
          }),
        }));

        await mutate(getNewCacheValue, { revalidate: false });
        await request;

        set(
          state => {
            state.linter.settings[name] = value;
            state.linter.isSaving[name] = false;
          },
          false,
          actionLog('ai.linter.updatePrompt', [name, value]),
        );
      } catch (error) {
        // @todo: notification
        // eslint-disable-next-line no-console
        console.error(error);
        set(
          state => {
            state.linter.isSaving[name] = false;
          },
          false,
          actionLog('ai.linter.updatePrompt', [name, value]),
        );
      }
    },
  },
});
